import { useState } from 'react';

export default function ContactLocation() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    partySize: '',
    message: '',
    interestedInParty: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('date', formData.date);
      formBody.append('partySize', formData.partySize);
      formBody.append('message', formData.message);
      formBody.append('interestedInParty', formData.interestedInParty ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d66vm0mj853lk6gjule0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          partySize: '',
          message: '',
          interestedInParty: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hours = [
    { day: 'Monday - Thursday', time: '4:00 PM - 12:00 AM' },
    { day: 'Friday', time: '4:00 PM - 2:00 AM' },
    { day: 'Saturday', time: '12:00 PM - 2:00 AM' },
    { day: 'Sunday', time: '12:00 PM - 10:00 PM' }
  ];

  return (
    <section id="contact" className="relative py-24 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] to-[#0d1f3c]"></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-400 mb-4">
            Location &amp; Contact
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-pink-500 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Contact Info & Hours */}
          <div>
            <div className="bg-gradient-to-br from-[#0d1f3c] to-[#0a1628] rounded-xl p-8 border border-cyan-500/20 mb-8">
              <h3 className="text-2xl font-bold text-cyan-400 mb-6 flex items-center">
                <i className="ri-map-pin-line mr-3"></i>
                Visit Us
              </h3>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start">
                  <i className="ri-map-pin-fill text-pink-400 mr-3 mt-1"></i>
                  <div>
                    <p className="font-bold text-white">Address</p>
                    <p>123 Arcade Avenue</p>
                    <p>Downtown District, ST 12345</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-phone-fill text-pink-400 mr-3 mt-1"></i>
                  <div>
                    <p className="font-bold text-white">Phone</p>
                    <p>(555) 123-4567</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-mail-fill text-pink-400 mr-3 mt-1"></i>
                  <div>
                    <p className="font-bold text-white">Email</p>
                    <p>hello@joystickspixelspour.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0d1f3c] to-[#0a1628] rounded-xl p-8 border border-cyan-500/20 mb-8">
              <h3 className="text-2xl font-bold text-orange-400 mb-6 flex items-center">
                <i className="ri-time-line mr-3"></i>
                Hours
              </h3>
              <div className="space-y-3">
                {hours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-cyan-500/10">
                    <span className="text-gray-300 font-medium">{schedule.day}</span>
                    <span className="text-cyan-400 font-bold">{schedule.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden border-2 border-cyan-500/30 h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.1841374024243!2d-73.98823492346618!3d40.75889097138558!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25855c6480299%3A0x55194ec5a1ae072e!2sTimes%20Square!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Location Map"
              ></iframe>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div>
            <div className="bg-gradient-to-br from-[#0d1f3c] to-[#0a1628] rounded-xl p-8 border border-cyan-500/20">
              <h3 className="text-2xl font-bold text-pink-400 mb-6 flex items-center">
                <i className="ri-message-3-line mr-3"></i>
                Get In Touch
              </h3>

              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a1628] border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors duration-300 text-sm"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a1628] border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors duration-300 text-sm"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a1628] border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors duration-300 text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-300 mb-2">
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-4 py-3 bg-[#0a1628] border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors duration-300 text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="partySize" className="block text-sm font-medium text-gray-300 mb-2">
                        Party Size
                      </label>
                      <input
                        type="number"
                        id="partySize"
                        name="partySize"
                        min="1"
                        value={formData.partySize}
                        onChange={(e) => setFormData({ ...formData, partySize: e.target.value })}
                        className="w-full px-4 py-3 bg-[#0a1628] border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors duration-300 text-sm"
                        placeholder="# of guests"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a1628] border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors duration-300 resize-none text-sm"
                      placeholder="Tell us about your event or inquiry..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="interestedInParty"
                      name="interestedInParty"
                      checked={formData.interestedInParty}
                      onChange={(e) => setFormData({ ...formData, interestedInParty: e.target.checked })}
                      className="w-5 h-5 bg-[#0a1628] border border-cyan-500/30 rounded text-cyan-500 focus:ring-cyan-500 focus:ring-2 cursor-pointer"
                    />
                    <label htmlFor="interestedInParty" className="ml-3 text-sm text-gray-300 cursor-pointer">
                      I'm interested in booking a party
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-cyan-500 to-pink-500 text-white text-lg font-bold rounded-lg hover:shadow-[0_0_30px_rgba(34,211,238,0.6)] transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-sm">
                      Thanks! We'll get back to you soon.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
